import { useCallback } from 'react';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { SearchInput, SelectInput } from '@/modules/forms';

import { VAULTS_FILTER_ALL_VALUE } from '../../constants';
import { useVaultsTableStore } from '../../hooks';
import type { VaultsTableFilters as VaultsTableFiltersState } from '../../types';
import { useVaultsFilterSelectProps } from './hooks/useVaultsFilterSelectProps';
import * as felaRules from './VaultsTableFilters.rules';

type VaultsFilterKey = Exclude<keyof VaultsTableFiltersState, 'search'>;

// The pill copy is plural ("Groups: All") while the parameter narrows to a single member, so the
// message ids cannot simply be derived from the keys.
const FILTERS = [
    { key: 'groups', messageId: 'vaults.filters.groups' },
    { key: 'users', messageId: 'vaults.filters.users' },
] as const satisfies readonly { key: VaultsFilterKey; messageId: string }[];

export const VaultsTableFilters = () => {
    const { css } = useFela();
    const { formatMessage } = useIntl();
    const { filters, setFilters } = useVaultsTableStore();

    const selectProps = useVaultsFilterSelectProps();

    const handleFilterChange = useCallback(
        (key: VaultsFilterKey, value: string) => {
            setFilters({ ...filters, [key]: value === VAULTS_FILTER_ALL_VALUE ? undefined : value });
        },
        [filters, setFilters],
    );

    return (
        <div className={css(felaRules.container)}>
            <div className={css(felaRules.filtersContainer)}>
                {FILTERS.map(({ key, messageId }) => (
                    <SelectInput
                        key={key}
                        {...selectProps[key]}
                        value={filters[key] ?? VAULTS_FILTER_ALL_VALUE}
                        onChange={value => handleFilterChange(key, value)}
                        customStyle={felaRules.selectButton}
                        labelRender={({ label }) => <FormattedMessage id={messageId} values={{ value: label }} />}
                    />
                ))}
            </div>

            <SearchInput
                placeholder={formatMessage({ id: 'vaults.filters.search.placeholder' })}
                customStyle={felaRules.searchInput}
                onChange={event => {
                    setFilters({ ...filters, search: event.target.value || undefined });
                }}
            />
        </div>
    );
};
